// The mixdown a browser save has to bring with it.
//
// A jam commit writes the arrangement and references the clips' audio, and
// that is the right minimum for people already in the room — they have the
// sound. A SEED PAGE is not that: it plays the master, draws its waveform and
// shows its length. Publishing without one produced a version that was
// correct and unplayable — 0:00, a flat line, a dead play button — which is
// what every version saved from the browser was (v5–v8 of
// `s_session-view-testing`, against v1–v4 from the extension, which all have
// one).
//
// So the browser renders it. It already holds every clip decoded for
// playback, and `renderMixdown` is the same offline render the mixdown
// download uses, through the same lane gates, levels and pans — what you hear
// is what is saved.
//
// The upload is the pool's ordinary path, the one a jam clip takes: hash the
// bytes, ask for a signed PUT, send them, and have the worker stream them back
// and check them against the hash before anything references them
// (`/api/jam/:id/files/:hash/verify`). A master is audio like any other and
// gets no shortcut.

import { encodeWaveform, peaksOf } from "@shared/audio/waveform.js";
import { encodeWav24 } from "@web-shared/lib/wav";
import { putPoolAudio, sha256Hex } from "./pool-audio";

/** What `POST /api/jam/:id/publish` wants alongside the note. */
export interface MasterUpload {
  hash: string;
  durationMs: number;
  waveform: string | null;
}

/** How far the render has got, for a dialog that would otherwise just hang. */
export type MasterProgress = "rendering" | "uploading" | "verifying";

/**
 * Render the room's mixdown, put it in the pool, and describe it for publish.
 *
 * Null when there is nothing to render — a room whose clips have no audio the
 * browser could load. That is not an error: the version still saves, it just
 * saves the way it always did, and a viewer can still draw a waveform later.
 */
export async function uploadMaster(
  jamId: string,
  render: () => Promise<AudioBuffer>,
  onProgress?: (stage: MasterProgress) => void,
): Promise<MasterUpload | null> {
  onProgress?.("rendering");
  const buffer = await render();
  if (buffer.length === 0 || buffer.sampleRate <= 0) return null;

  const wav = new Uint8Array(encodeWav24(buffer));
  const hash = await sha256Hex(wav);
  const durationMs = Math.round((buffer.length / buffer.sampleRate) * 1000);
  const waveform = waveformOf(buffer);

  onProgress?.("uploading");
  await putPoolAudio(jamId, wav);
  return { hash, durationMs, waveform };
}

/** The master's peaks, in the same encoding the extension and the backfill
 * write (shared/audio/waveform.ts) — so a card drawn from this and a card
 * drawn from an uploaded seed are the same picture. */
function waveformOf(buffer: AudioBuffer): string | null {
  try {
    const channels = buffer.numberOfChannels;
    const frames = buffer.length;
    const samples = new Float32Array(frames * channels);
    for (let ch = 0; ch < channels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < frames; i++) samples[i * channels + ch] = data[i]!;
    }
    return encodeWaveform(peaksOf({ samples, channels, frames }));
  } catch {
    // A waveform is a nicety; a viewer draws one later if this fails
    // (account/waveform-backfill.ts). Never fail a save over it.
    return null;
  }
}
